import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { 
 Loader2, 
 Clock, 
 CheckCircle, 
 XCircle, 
 ArrowLeft, 
 Search, 
 Plus, 
 Briefcase, 
 Users, 
 MapPin, 
 Calendar,
 Filter,
 BarChart3,
 ChevronRight,
 MoreVertical
} from "lucide-react";
import api from "../../api/client";
import { Bar } from "react-chartjs-2";
import {
 Chart as ChartJS,
 CategoryScale,
 LinearScale,
 BarElement,
 Title,
 Tooltip,
 Legend,
} from "chart.js";

ChartJS.register(
 CategoryScale,
 LinearScale,
 BarElement,
 Title,
 Tooltip,
 Legend
);

export default function RecruiterJobsOverview() {
 const navigate = useNavigate();
 const [jobs, setJobs] = useState([]);
 const [filteredJobs, setFilteredJobs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState("ALL");
 const [currentPage, setCurrentPage] = useState(1);
 const itemsPerPage = 6;

 useEffect(() => {
 const fetchJobs = async () => {
 try {
 const res = await api.get("/recruiter/jobs");
 setJobs(res.data);
 } catch (err) {
 console.error(err);
 } finally {
 setLoading(false);
 }
 };
 fetchJobs();
 }, []);

 const handleFilterAndSearch = useCallback(() => {
 let data = [...jobs];

 if (statusFilter !== "ALL") {
 data = data.filter((j) => j.status === statusFilter);
 }

 if (search.trim()) {
 data = data.filter(
 (j) =>
 j.title.toLowerCase().includes(search.toLowerCase()) ||
 j.location.toLowerCase().includes(search.toLowerCase())
 );
 }

 setFilteredJobs(data);
 setCurrentPage(1);
 }, [jobs, statusFilter, search]);

 useEffect(() => {
 handleFilterAndSearch();
 }, [handleFilterAndSearch]);

 const stats = useMemo(() => {
 const total = jobs.length;
 const approved = jobs.filter(j => j.status === "APPROVED").length;
 const pending = jobs.filter(j => j.status === "PENDING").length;
 const rejected = jobs.filter(j => j.status === "REJECTED").length;
 const totalApplicants = jobs.reduce((acc, job) => acc + (job.applications?.length || 0), 0);
 
 return { total, approved, pending, rejected, totalApplicants };
 }, [jobs]);

 const chartData = useMemo(() => {
 return {
 labels: ["Pending", "Approved", "Rejected"],
 datasets: [
 {
 label: "Jobs by Status",
 data: [stats.pending, stats.approved, stats.rejected],
 backgroundColor: [
 "rgba(250, 204, 21, 0.6)", // yellow
 "rgba(34, 197, 94, 0.6)", // green
 "rgba(239, 68, 68, 0.6)", // red
 ],
 borderColor: [
 "rgb(250, 204, 21)",
 "rgb(34, 197, 94)",
 "rgb(239, 68, 68)",
 ],
 borderWidth: 2,
 borderRadius: 8,
 hoverBackgroundColor: [
 "rgba(250, 204, 21, 0.8)",
 "rgba(34, 197, 94, 0.8)",
 "rgba(239, 68, 68, 0.8)",
 ],
 },
 ],
 };
 }, [stats]);

 const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
 const paginatedJobs = filteredJobs.slice(
 (currentPage - 1) * itemsPerPage,
 currentPage * itemsPerPage
 );

 const getStatusBadge = (status) => {
 switch (status) {
 case "APPROVED":
 return <Badge className="bg-green-500/100/10 text-green-500 border-green-500/20 hover:bg-green-500/100/20 transition-colors">Approved</Badge>;
 case "PENDING":
 return <Badge variant="secondary" className="bg-yellow-500/100/10 text-yellow-500 border-yellow-500/20 hover:bg-yellow-500/100/20 transition-colors">Pending</Badge>;
 case "REJECTED":
 return <Badge variant="destructive" className="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20 transition-colors">Rejected</Badge>;
 default:
 return <Badge variant="outline">{status}</Badge>;
 }
 };

 if (loading) {
 return (
 <div className="flex flex-col items-center justify-center min-h-screen ">
 
 <div className=" flex flex-col items-center">
 <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
 <p className="text-muted-foreground animate-pulse font-medium">Loading your career gateway...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen text-foreground py-8 px-4 sm:px-6 lg:px-12 transition-colors duration-500">
 
 
 <div className="max-w-7xl mx-auto space-y-8">
 {/* Navigation & Actions */}
 <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
 <div className="space-y-1">
 <button
 onClick={() => navigate("/recruiter/dashboard")}
 className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-300 group mb-2"
 >
 <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
 Back to Dashboard
 </button>
 <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/70">
 Manage Your Jobs
 </h1>
 <p className="text-muted-foreground text-lg">
 Monitor, track, and manage all your active job postings.
 </p>
 </div>
 
 <Button 
 onClick={() => navigate("/recruiter/post-job")}
 variant="premium"
 size="lg" 
 className="shadow-xl shadow-primary/20 group transform transition-all duration-300 hover:scale-105 active:scale-95"
 >
 <Plus className="w-5 h-5 mr-2 group-hover:rotate-90 transition-transform duration-300" />
 Post New Job
 </Button>
 </div>

 {/* Stats Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 <StatCard 
 title="Total Jobs" 
 value={stats.total} 
 icon={<Briefcase className="w-5 h-5" />} 
 color="blue"
 delay={0.1}
 />
 <StatCard 
 title="Active Jobs" 
 value={stats.approved} 
 icon={<CheckCircle className="w-5 h-5" />} 
 color="green"
 delay={0.2}
 />
 <StatCard 
 title="Pending Jobs" 
 value={stats.pending} 
 icon={<Clock className="w-5 h-5" />} 
 color="yellow"
 delay={0.3}
 />
 {/* <StatCard 
 title="Total Applicants" 
 value={stats.totalApplicants} 
 icon={<Users className="w-5 h-5" />} 
 color="purple"
 delay={0.4}
 /> */}
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
 {/* Analytics Chart */}
 <motion.div 
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 className="lg:col-span-1"
 >
 <Card className="glass h-full shadow-2xl border-border/50 overflow-hidden">
 <CardHeader className="pb-2">
 <div className="flex items-center gap-2">
 <BarChart3 className="w-5 h-5 text-primary" />
 <CardTitle>Job Statistics</CardTitle>
 </div>
 <CardDescription>Visual breakdown by status</CardDescription>
 </CardHeader>
 <CardContent className="h-[300px] flex items-center justify-center p-6">
 <Bar
 data={chartData}
 options={{
 responsive: true,
 maintainAspectRatio: false,
 plugins: {
 legend: { display: false },
 tooltip: {
 backgroundColor: "rgba(0, 0, 0, 0.8)",
 padding: 12,
 cornerRadius: 8,
 titleFont: { size: 14, weight: 'bold' },
 bodyFont: { size: 13 }
 }
 },
 scales: {
 x: { grid: { display: false }, ticks: { color: "hsl(var(--muted-foreground))" } },
 y: { 
 beginAtZero: true, 
 grid: { color: "rgba(255, 255, 255, 0.05)" },
 ticks: { color: "hsl(var(--muted-foreground))", precision: 0 } 
 },
 },
 }}
 />
 </CardContent>
 </Card>
 </motion.div>

 {/* Jobs List with Search & Filter */}
 <motion.div 
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="lg:col-span-2 space-y-6"
 >
 <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
 <div className="relative w-full sm:max-w-md group">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
 <Input
 placeholder="Search jobs by title or location..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="pl-10 bg-card/30 backdrop-blur-sm border-border/50 focus:ring-primary h-11 transition-all duration-300"
 />
 </div>
 <div className="flex items-center gap-2 w-full sm:w-auto">
 <div className="p-2 rounded-lg bg-card/50 border border-border/50">
 <Filter className="w-4 h-4 text-muted-foreground" />
 </div>
 <select
 value={statusFilter}
 onChange={(e) => setStatusFilter(e.target.value)}
 className="bg-card/30 backdrop-blur-sm text-foreground border border-border/50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 flex-1 sm:w-40 transition-all duration-300"
 >
 <option value="ALL">All Statuses</option>
 <option value="PENDING">Pending</option>
 <option value="APPROVED">Approved</option>
 <option value="REJECTED">Rejected</option>
 </select>
 </div>
 </div>

 <div className="space-y-4">
 <AnimatePresence mode="popLayout">
 {paginatedJobs.length > 0 ? (
 paginatedJobs.map((job, idx) => (
 <JobRow 
 key={job.id} 
 job={job} 
 navigate={navigate} 
 getStatusBadge={getStatusBadge}
 index={idx}
 />
 ))
 ) : (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 className="text-center py-20 bg-card/20 rounded-2xl border-2 border-dashed border-border/50"
 >
 <Briefcase className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
 <h3 className="text-xl font-semibold">No jobs found</h3>
 <p className="text-muted-foreground">Try adjusting your search or filters.</p>
 </motion.div>
 )}
 </AnimatePresence>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex justify-center items-center gap-2 pt-4">
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === 1}
 onClick={() => setCurrentPage((prev) => prev - 1)}
 className="bg-card/50 backdrop-blur-sm border-border/50"
 >
 Previous
 </Button>
 <div className="flex items-center gap-1">
 {Array.from({ length: totalPages }, (_, i) => (
 <Button
 key={i}
 variant={currentPage === i + 1 ? "premium" : "ghost"}
 size="sm"
 onClick={() => setCurrentPage(i + 1)}
 className="w-10 h-10"
 >
 {i + 1}
 </Button>
 ))}
 </div>
 <Button
 variant="outline"
 size="sm"
 disabled={currentPage === totalPages}
 onClick={() => setCurrentPage((prev) => prev + 1)}
 className="bg-card/50 backdrop-blur-sm border-border/50"
 >
 Next
 </Button>
 </div>
 )}
 </motion.div>
 </div>
 </div>
 </div>
 );
}

function StatCard({ title, value, icon, color, delay }) {
 const colorMap = {
 blue: "from-blue-500/20 to-blue-600/5 text-blue-500 border-blue-500/20",
 green: "from-green-500/20 to-green-600/5 text-green-500 border-green-500/20",
 yellow: "from-yellow-500/20 to-yellow-600/5 text-yellow-500 border-yellow-500/20",
 purple: "from-purple-500/20 to-purple-600/5 text-purple-500 border-purple-500/20",
 };

 return (
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay }}
 whileHover={{ y: -5, transition: { duration: 0.2 } }}
 >
 <Card className={`glass bg-gradient-to-br ${colorMap[color]} shadow-lg overflow-hidden relative group`}>
 <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-125 transition-transform duration-500">
 {icon}
 </div>
 <CardContent className="p-6 space-y-2">
 <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider opacity-70">
 {icon}
 {title}
 </div>
 <div className="text-3xl font-bold tracking-tight">{value}</div>
 </CardContent>
 </Card>
 </motion.div>
 );
}

function JobRow({ job, navigate, getStatusBadge, index }) {
 return (
 <motion.div
 layout
 initial={{ opacity: 0, y: 10 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, scale: 0.95 }}
 transition={{ delay: index * 0.05 }}
 whileHover={{ scale: 1.01 }}
 className="group"
 >
 <Card 
 className="glass hover:shadow-2xl hover:shadow-primary/5 transition-all duration-300 border-border/50 cursor-pointer overflow-hidden group/card"
 onClick={() => navigate(`/jobs/${job.id}`)}
 >
 <CardContent className="p-0">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4">
 <div className="flex items-start gap-4 flex-1">
 <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 transition-all duration-300 group-hover/card:bg-primary/20 group-hover/card:scale-110">
 <Briefcase className="w-6 h-6 text-primary" />
 </div>
 <div className="space-y-1">
 <h3 className="font-bold text-lg text-foreground group-hover/card:text-primary transition-colors">{job.title}</h3>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground font-medium">
 <div className="flex items-center gap-1.5">
 <MapPin className="w-3.5 h-3.5" />
 {job.location}
 </div>
 <div className="flex items-center gap-1.5">
 <Calendar className="w-3.5 h-3.5" />
 {new Date(job.createdAt).toLocaleDateString()}
 </div>
 <div className="flex items-center gap-1.5">
 <Users className="w-3.5 h-3.5" />
 {job.applications?.length || 0} applicants
 </div>
 </div>
 </div>
 </div>
 
 <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-4 sm:pt-0 border-border/50">
 <div className="flex flex-col items-start sm:items-end gap-1">
 {getStatusBadge(job.status)}
 <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-bold px-1">
 Posted Status
 </span>
 </div>
 
 <div 
 onClick={(e) => {
 e.stopPropagation();
 navigate(`/recruiter/jobs/${job.id}/applicants`);
 }}
 className="w-10 h-10 rounded-xl bg-card/80 border border-border/50 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group/btn shadow-sm"
 title="View Applicants"
 >
 <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-0.5 transition-transform" />
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </motion.div>
 );
}
